import {
  GoogleMap,
  DirectionsService,
  DirectionsRenderer,
  LoadScriptNext,
  MarkerClusterer,
} from "@react-google-maps/api";

import React, {
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useRef,
  useState,
  useMemo,
} from "react";

import isEmpty from "lodash.isempty";
import { observer } from "mobx-react";
import * as PropTypes from "prop-types";
import styled, { useTheme } from "styled-components";

import { useApi } from "../../../contexts/ApiContext";
import ComponentContext from "../../../contexts/ComponentContext";
import { useStores } from "../../../contexts/StoreContext";
import Markdown from "../../styles/Markdown";
import Loader from "../Loader/Loader";
import MapInfoBox from "./MapInfoBox/MapInfoBox";
import MapMarkers from "./MapMarkers/MapMarkers";
import normalizeMarkerDetails, {
  normalizeVacancyPoint,
} from "./normalizeMarkerDetails/normalizeMarkerDetails";

const defaultInfoBoxState = {
  city: "",
  coords: {},
  displayName: "",
  formattedAddress: "",
  id: "",
  isOpen: false,
  logoUrlFull: "",
  totalVacancies: 0,
};

const OPEN_INFO_BOX = "OPEN_INFO_BOX";
const CLOSE_INFO_BOX = "CLOSE_INFO_BOX";

function infoBoxReducer(state, action) {
  const { type, payload } = action;

  switch (type) {
    case OPEN_INFO_BOX:
      return { ...state, ...payload, isOpen: true };
    case CLOSE_INFO_BOX:
      return { ...defaultInfoBoxState };
    default:
      return state;
  }
}

const StyledFallback = styled.div`
  min-height: 40vh;
`;

function GMap({
  styles,
  apiKey,
  markers,
  height,
  destination,
  origin,
  onDirectionsLoad,
  travelMode,
  markerIconUrl,
}) {
  const theme = useTheme();
  const [infoBoxState, dispatchInfoBox] = useReducer(
    infoBoxReducer,
    defaultInfoBoxState
  );

  const count = useRef(0);

  const [directions, setDirections] = useState();
  const [map, setMap] = useState(null);
  const hasOnlyOneMarker = markers.length === 1;

  const clusterStyles = useMemo(() => {
    const primaryColor = theme?.primaryColor || "#E31E24";

    return [
      {
        textColor: "white",
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
          <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40">
            <circle cx="20" cy="20" r="18" fill="${primaryColor}" />
          </svg>
        `)}`,
        height: 40,
        width: 40,
        textSize: 13,
        fontWeight: "bold",
      },
      {
        textColor: "white",
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48">
            <circle cx="24" cy="24" r="22" fill="${primaryColor}" />
          </svg>
        `)}`,
        height: 48,
        width: 48,
        textSize: 14,
        fontWeight: "bold",
      },
      {
        textColor: "white",
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
          <svg xmlns="http://www.w3.org/2000/svg" width="56" height="56">
            <circle cx="28" cy="28" r="26" fill="${primaryColor}" />
          </svg>
        `)}`,
        height: 56,
        width: 56,
        textSize: 15,
        fontWeight: "bold",
      },
    ];
  }, [theme?.primaryColor]);

  useEffect(() => {
    count.current = 0;
  }, [travelMode, destination]);

  const directionsCallback = (response) => {
    if (response && response.status === "OK" && count.current === 0) {
      count.current += 1;
      setDirections(response);
      onDirectionsLoad(response);
    }
  };

  const handleMarkerClick = (marker) => {
    if (marker.id) {
      dispatchInfoBox({
        payload: { ...marker },
        type: OPEN_INFO_BOX,
      });
    }
  };

  const mapCenter = hasOnlyOneMarker
    ? markers[0].coords
    : { lat: 51.4857, lng: -0.96696 };

  useEffect(() => {
    if (map && !isEmpty(markers)) {
      const bounds = new window.google.maps.LatLngBounds();

      markers.forEach((marker) => {
        bounds.extend(
          new window.google.maps.LatLng(marker.coords.lat, marker.coords.lng)
        );
      });
      map.fitBounds(bounds);
    }
  }, [markers, map]);

  const onLoad = useCallback((mapInstance) => {
    setMap(mapInstance);
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  if (isEmpty(markers)) {
    return null;
  }

  return (
    <LoadScriptNext
      googleMapsApiKey={apiKey}
      language="en"
      loadingElement={<Loader minHeight={height} />}
    >
      <div className="h-100 d-flex flex-column">
        <div className="flex-grow-1">
          <GoogleMap
            center={mapCenter}
            className="flex-grow-1 flex-shrink-1"
            mapContainerStyle={{ height, width: "100%" }}
            onLoad={onLoad}
            onUnmount={onUnmount}
            options={{
              disableDefaultUI: true,
              fullscreenControl: true,
              styles,

              zoomControl: true,
            }}
            zoom={hasOnlyOneMarker ? 17 : 6}
          >
            {!isEmpty(markers) && (
              <MarkerClusterer
                options={{
                  styles: clusterStyles,
                  minimumClusterSize: 2,
                  gridSize: 40,
                }}
              >
                {(markerClusterer) => (
                  <div>
                    <MapMarkers
                      markers={markers}
                      onMarkerClick={handleMarkerClick}
                      clusterer={markerClusterer}
                      markerIconUrl={markerIconUrl}
                    />
                  </div>
                )}
              </MarkerClusterer>
            )}
            {infoBoxState.isOpen && (
              <MapInfoBox
                onClose={() => dispatchInfoBox({ type: CLOSE_INFO_BOX })}
                state={infoBoxState}
              />
            )}

            {directions && (
              <DirectionsRenderer
                options={{
                  directions,
                }}
              />
            )}
          </GoogleMap>
          {destination && origin && travelMode && (
            <DirectionsService
              callback={directionsCallback}
              options={{
                destination,
                origin,
                travelMode,
              }}
            />
          )}
        </div>
      </div>
    </LoadScriptNext>
  );
}

GMap.propTypes = {
  apiKey: PropTypes.string.isRequired,
  destination: PropTypes.arrayOf(
    PropTypes.shape({
      lat: PropTypes.number,
      lng: PropTypes.number,
    })
  ),
  height: PropTypes.string,
  markers: PropTypes.arrayOf(
    PropTypes.shape({
      city: PropTypes.string,
      coords: PropTypes.shape({
        lat: PropTypes.number,
        lng: PropTypes.number,
      }),
      displayName: PropTypes.string,
      formattedAddress: PropTypes.string,
      id: PropTypes.string,
      logoUrlFull: PropTypes.string,
      totalVacancies: PropTypes.number,
    })
  ),
  markerIconUrl: PropTypes.string,
  onDirectionsLoad: PropTypes.func,
  origin: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
  }),
  styles: PropTypes.arrayOf(
    PropTypes.shape({
      elementType: PropTypes.string,
      featureType: PropTypes.string,
      stylers: PropTypes.arrayOf(PropTypes.shape({})),
    })
  ),
  travelMode: PropTypes.string,
};

GMap.defaultProps = {
  destination: undefined,
  height: "500px",
  markerIconUrl: undefined,
  markers: [],
  onDirectionsLoad: () => {},
  origin: undefined,
  styles: undefined,
  travelMode: undefined,
};

function Map(props) {
  const { withMarkers, showText = true, singlePoint } = props;

  const { text, useTextAsCover, markerIcon, loadMarkersOnMount = false } = useContext(ComponentContext);
  const markerIconUrl = markerIcon?.url;

  const { configStore } = useStores();
  const { googleMapsAPIKey, additionalConfiguration } = configStore.config;
  const [markerDetails, setMarkerDetails] = useState([]);
  const api = useApi();

  useEffect(() => {
    if (withMarkers|| loadMarkersOnMount) {
      api
        .getVacancies({}, { fields: ["company", "location"], limit: 1000 })
        .then((response) => {
          const details = normalizeMarkerDetails(response.data.results);
          setMarkerDetails(details);
        })
        .catch();
    } else if (!isEmpty(singlePoint)) {
      const pointDetails = [normalizeVacancyPoint(singlePoint)];
      setMarkerDetails(pointDetails);
    }
  }, []);

  return googleMapsAPIKey ? (
    <>
      {showText && !useTextAsCover && <Markdown>{text}</Markdown>}
      <GMap
        {...props}
        apiKey={googleMapsAPIKey}
        markers={markerDetails}
        styles={additionalConfiguration?.mapStyles}
        markerIconUrl={markerIconUrl}
      />
    </>
  ) : (
    <StyledFallback className="text-center align-items-center d-flex justify-content-center">
      <h2>
        Sorry, the map is currently not available.
        <br />
        Please try again later.
      </h2>
    </StyledFallback>
  );
}

Map.propTypes = {
  apiKey: PropTypes.string,
  showText: PropTypes.bool,
  singlePoint: PropTypes.shape({
    coords: PropTypes.shape({
      lat: PropTypes.number,
      lng: PropTypes.number,
    }),
  }),
  text: PropTypes.string,
    withMarkers: PropTypes.bool,
};

Map.defaultProps = {
  apiKey: null,
  showText: true,
  singlePoint: undefined,
  text: "null",
  withMarkers: false,
};

export default observer(Map);
